import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Package, Tag, CalendarClock, Ticket, User as UserIcon, X, Flame } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatNZD } from "@/lib/format";
import { buildAdPath } from "@/lib/slugs";
import { readLocalRecent, writeLocalRecent, recordRecentSearch } from "@/lib/searchRecent";

/**
 * Iter-277 §1 — Whole-site top-bar search.
 *
 * Renders below the Header search input as a floating panel. Fires
 * `GET /api/search/all?q=<term>&country=NZ` (public, browser UA)
 * whenever the debounced query is >= 2 chars. Groups: products,
 * classifieds, events, deals, sellers.
 *
 * Empty groups are omitted. Non-fixed price ads show the `price_label`
 * instead of a `$0`. Business sellers get a 🏢 chip inline. Clicks
 * close the panel and route to the correct detail path per group.
 *
 * Contract shape (each list is capped by the backend):
 *   { q,
 *     products:[≤8:  {id,name,image,price_nzd,seller_name,category}],
 *     classifieds:[≤8:{id,title,price,price_type,image,category_name,
 *                     category_slug,city,seller_name,seller_is_business,
 *                     condition}],
 *     events:[≤6:    {id,title,image_url,starts_at,city,venue,price_label}],
 *     deals:[≤6:     {id,title,image,price,price_label,city,posted_by}],
 *     sellers:[≤6:   {id,name,photo,is_business,verified,active_listings}] }
 */
const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;
const MIGRATED_KEY = "allsale_recent_migrated_v1";
const TRENDING_STALE_MS = 10 * 60 * 1000; // 10 min

export default function TopSearchResults({ query, onNavigate, open }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isSignedIn = Boolean(user?.id);

  // Debounce so we don't hammer the endpoint per keystroke.
  const [debounced, setDebounced] = useState(query || "");
  useEffect(() => {
    const t = setTimeout(() => setDebounced((query || "").trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const enabled = open && debounced.length >= MIN_CHARS;
  const q = useQuery({
    queryKey: ["top-search", debounced],
    queryFn: async () => {
      const { data } = await sharedApi.get("/search/all", {
        params: { q: debounced, country: "NZ" },
      });
      return data;
    },
    enabled,
    staleTime: 15_000,
    // Iter-277 backend-pod recommendation: silent retry before the
    // error state so a transient restart window (~1s) doesn't show a
    // scary "unavailable" toast to the user.
    retry: 2,
    retryDelay: (attempt) => 400 * (attempt + 1),
  });

  // ── Iter-282 §1 — Recent chips (server for signed-in, local for guests) ──
  const recentQ = useQuery({
    queryKey: ["search-recent", isSignedIn],
    enabled: open && isSignedIn,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await sharedApi.get("/search/recent");
      return Array.isArray(data?.terms) ? data.terms.slice(0, 8) : [];
    },
  });

  // Local list for guests (reads on panel open so writes elsewhere reflect).
  const [localRecent, setLocalRecent] = useState(() => readLocalRecent());
  useEffect(() => {
    if (open && !isSignedIn) setLocalRecent(readLocalRecent());
  }, [open, isSignedIn]);

  // One-shot migration: on first sign-in, ship the guest history to the
  // server, then clear the local copy so the two sources never drift.
  // In-flight ref guards against React strict-mode double-invocation
  // firing two identical POSTs before MIGRATED_KEY is written.
  // On POST failure we DELIBERATELY keep local recents + skip the marker
  // so a transient/401 error doesn't permanently discard guest history —
  // the next mount will retry.
  const migrationRunning = useRef(false);
  useEffect(() => {
    if (!isSignedIn) return;
    if (localStorage.getItem(MIGRATED_KEY)) return;
    if (migrationRunning.current) return;
    migrationRunning.current = true;
    const terms = readLocalRecent();
    (async () => {
      if (!terms.length) {
        // Nothing to migrate — mark done so we don't re-check every mount.
        localStorage.setItem(MIGRATED_KEY, "1");
        return;
      }
      try {
        await sharedApi.post("/search/recent", { terms });
        // Only clear + mark on confirmed success.
        localStorage.setItem(MIGRATED_KEY, "1");
        writeLocalRecent([]);
        qc.invalidateQueries({ queryKey: ["search-recent"] });
      } catch {
        // Preserve local recents + leave marker unset — a later mount
        // (or the next login) will retry the migration.
        migrationRunning.current = false;
      }
    })();
  }, [isSignedIn, qc]);

  const recent = isSignedIn ? (recentQ.data || []) : localRecent;

  const submitRecent = (term) => {
    if (!term) return;
    recordRecentSearch(term, isSignedIn, qc);
    if (!isSignedIn) setLocalRecent(readLocalRecent());
  };

  const deleteRecentMut = useMutation({
    mutationFn: async (term) => {
      if (isSignedIn) {
        await sharedApi.delete("/search/recent", { params: { term } });
      } else {
        writeLocalRecent(readLocalRecent().filter((t) => t.toLowerCase() !== term.toLowerCase()));
      }
      return term;
    },
    onSuccess: () => {
      if (isSignedIn) qc.invalidateQueries({ queryKey: ["search-recent"] });
      else setLocalRecent(readLocalRecent());
    },
  });

  const clearRecentMut = useMutation({
    mutationFn: async () => {
      if (isSignedIn) {
        await sharedApi.delete("/search/recent");
      } else {
        writeLocalRecent([]);
      }
    },
    onSuccess: () => {
      if (isSignedIn) qc.invalidateQueries({ queryKey: ["search-recent"] });
      else setLocalRecent([]);
    },
  });

  // ── Iter-282 §2 — Trending chips (public, cache ~10 min) ──
  const trendingQ = useQuery({
    queryKey: ["search-trending", "classifieds"],
    enabled: open,
    staleTime: TRENDING_STALE_MS,
    retry: 1,
    queryFn: async () => {
      const { data } = await sharedApi.get("/search/trending", {
        params: { scope: "classifieds" },
      });
      return Array.isArray(data?.terms) ? data.terms.slice(0, 10) : [];
    },
  });
  const trending = trendingQ.data || [];

  const groups = useMemo(() => q.data || {}, [q.data]);
  const totalCount =
    (groups.products?.length || 0) +
    (groups.classifieds?.length || 0) +
    (groups.events?.length || 0) +
    (groups.deals?.length || 0) +
    (groups.sellers?.length || 0);

  if (!open) return null;

  // ── Empty-state panel: Recent + Trending chips ──
  if (debounced.length < MIN_CHARS) {
    const hasAny = recent.length > 0 || trending.length > 0;
    if (!hasAny) return null;
    return (
      <div
        data-testid="top-search-panel"
        className="absolute left-0 right-0 top-full mt-2 z-40 rounded-2xl bg-white border border-soft shadow-card p-3 space-y-3"
      >
        {recent.length > 0 && (
          <div data-testid="search-recent-row">
            <div className="flex items-center justify-between px-1 pb-1.5">
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Recent</span>
              <button
                type="button"
                onClick={() => clearRecentMut.mutate()}
                disabled={clearRecentMut.isPending}
                data-testid="search-recent-clear-all"
                className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-[color:var(--brand-blue)]"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {recent.map((term) => (
                <RecentChip
                  key={term}
                  term={term}
                  onSelect={() => { submitRecent(term); onNavigate?.(); nav(`/search?q=${encodeURIComponent(term)}`); }}
                  onDelete={() => deleteRecentMut.mutate(term)}
                />
              ))}
            </div>
          </div>
        )}
        {trending.length > 0 && (
          <div data-testid="search-trending-row">
            <div className="px-1 pb-1.5 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
              <Flame className="w-3 h-3 text-[color:var(--brand-orange)]" /> Trending
            </div>
            <div className="flex flex-wrap gap-1.5">
              {trending.map((term) => (
                <button
                  key={term}
                  type="button"
                  data-testid={`trending-chip-${term.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  onClick={() => { submitRecent(term); onNavigate?.(); nav(`/search?q=${encodeURIComponent(term)}`); }}
                  className="inline-flex items-center rounded-full bg-slate-100 hover:bg-[color:var(--brand-blue-wash)] hover:text-[color:var(--brand-blue)] text-slate-700 text-xs font-semibold px-3 py-1"
                >
                  {term}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const goto = (to) => {
    submitRecent(debounced);
    onNavigate?.();
    nav(to);
  };

  return (
    <div
      data-testid="top-search-panel"
      className="absolute left-0 right-0 top-full mt-2 z-40 rounded-2xl bg-white border border-soft shadow-card overflow-hidden"
    >
      {q.isLoading && (
        <div className="p-6 grid place-items-center text-slate-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      )}
      {!q.isLoading && !q.isError && totalCount === 0 && (
        <div className="p-6 text-center text-sm text-slate-500" data-testid="top-search-empty">
          No matches for &ldquo;<span className="font-semibold text-slate-800">{debounced}</span>&rdquo;.
        </div>
      )}
      {q.isError && (
        <div className="p-6 text-center text-sm text-red-500">Search unavailable — please try again shortly.</div>
      )}
      <div className="max-h-[70vh] overflow-y-auto divide-y divide-soft">
        {!!groups.classifieds?.length && (
          <Group icon={<Tag className="w-3.5 h-3.5" />} label="Classifieds">
            {groups.classifieds.map((c) => (
              <Row
                key={`clf-${c.id}`}
                testId={`search-classified-${c.id}`}
                image={c.image}
                title={c.title}
                meta={[c.category_name, c.city].filter(Boolean).join(" · ")}
                price={c.price_type && c.price_type !== "fixed" ? (c.price_label || c.price_type) : (c.price ? formatNZD(c.price) : null)}
                right={c.seller_is_business ? <BizChip /> : null}
                onClick={() => goto(buildAdPath({ id: c.id, title: c.title, category_slug: c.category_slug }))}
              />
            ))}
          </Group>
        )}
        {!!groups.products?.length && (
          <Group icon={<Package className="w-3.5 h-3.5" />} label="Marketplace">
            {groups.products.map((p) => (
              <Row
                key={`prod-${p.id}`}
                testId={`search-product-${p.id}`}
                image={p.image}
                title={p.name}
                meta={[p.category, p.seller_name].filter(Boolean).join(" · ")}
                price={p.price_nzd ? formatNZD(p.price_nzd) : null}
                /* Iter-277f — Marketplace/shop products live on the Bazaar
                   side, not on this classifieds site. Route through the
                   backend's geo-aware 302 (`/api/go/bazaar?path=…`) so
                   IN visitors hit allsale.net.in and everyone else hits
                   nz.allsale.co.nz. Plain <a> so the browser follows
                   the redirect naturally in a new tab. */
                href={`/api/go/bazaar?path=/product/${encodeURIComponent(p.id)}`}
                external
                onClick={() => { submitRecent(debounced); onNavigate?.(); }}
              />
            ))}
          </Group>
        )}
        {!!groups.events?.length && (
          <Group icon={<CalendarClock className="w-3.5 h-3.5" />} label="Events">
            {groups.events.map((e) => (
              <Row
                key={`evt-${e.id}`}
                testId={`search-event-${e.id}`}
                image={e.image_url}
                title={e.title}
                meta={[e.venue, e.city].filter(Boolean).join(" · ")}
                price={e.price_label || null}
                onClick={() => goto(`/events/${e.id}`)}
              />
            ))}
          </Group>
        )}
        {!!groups.deals?.length && (
          <Group icon={<Ticket className="w-3.5 h-3.5" />} label="Deals">
            {groups.deals.map((d) => (
              <Row
                key={`deal-${d.id}`}
                testId={`search-deal-${d.id}`}
                image={d.image}
                title={d.title}
                meta={[d.posted_by, d.city].filter(Boolean).join(" · ")}
                price={d.price_label || (d.price ? formatNZD(d.price) : null)}
                onClick={() => goto(`/deals/${d.id}`)}
              />
            ))}
          </Group>
        )}
        {!!groups.sellers?.length && (
          <Group icon={<UserIcon className="w-3.5 h-3.5" />} label="Sellers">
            {groups.sellers.map((s) => (
              <Row
                key={`sel-${s.id}`}
                testId={`search-seller-${s.id}`}
                image={s.photo}
                round
                title={s.name}
                meta={s.active_listings != null ? `${s.active_listings} active listing${s.active_listings === 1 ? "" : "s"}` : ""}
                right={s.is_business ? <BizChip /> : null}
                onClick={() => goto(`/seller/${s.id}`)}
              />
            ))}
          </Group>
        )}
      </div>
      {totalCount > 0 && (
        <button
          type="button"
          onClick={() => goto(`/search?q=${encodeURIComponent(debounced)}`)}
          data-testid="top-search-see-all"
          className="w-full border-t border-soft py-2.5 text-xs font-semibold text-[color:var(--brand-blue)] hover:bg-slate-50"
        >
          See all results for &ldquo;{debounced}&rdquo;
        </button>
      )}
    </div>
  );
}

function Group({ icon, label, children }) {
  return (
    <div>
      <div className="px-4 pt-3 pb-1 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">
        {icon} {label}
      </div>
      <ul>{children}</ul>
    </div>
  );
}

function Row({ testId, image, title, meta, price, right, onClick, round, href, external }) {
  const imgRef = useRef(null);
  const inner = (
    <>
      <span className="shrink-0">
        {image ? (
          <img
            ref={imgRef}
            src={image}
            alt=""
            referrerPolicy="no-referrer"
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
            className={`w-10 h-10 object-cover bg-slate-100 ${round ? "rounded-full" : "rounded-lg"}`}
          />
        ) : (
          <div className={`w-10 h-10 bg-slate-100 grid place-items-center text-slate-400 ${round ? "rounded-full" : "rounded-lg"}`}>
            <Package className="w-4 h-4" />
          </div>
        )}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-semibold text-slate-800 truncate">{title}</span>
        {meta && <span className="block text-[11px] text-slate-500 truncate">{meta}</span>}
      </span>
      {price && <span className="text-xs font-bold text-slate-800 shrink-0">{price}</span>}
      {right && <span className="shrink-0">{right}</span>}
    </>
  );
  const cls = "w-full text-left flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors";
  if (href) {
    return (
      <li>
        <a
          href={href}
          data-testid={testId}
          onClick={onClick}
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          className={cls}
        >
          {inner}
        </a>
      </li>
    );
  }
  return (
    <li>
      <button type="button" onClick={onClick} data-testid={testId} className={cls}>
        {inner}
      </button>
    </li>
  );
}

function BizChip() {
  return (
    <span
      title="Business seller"
      className="inline-flex items-center rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 px-1.5 py-0.5 text-[9px] font-bold tracking-widest"
    >
      🏢
    </span>
  );
}

function RecentChip({ term, onSelect, onDelete }) {
  return (
    <span
      className="group inline-flex items-center rounded-full bg-slate-100 text-slate-700 text-xs font-semibold pl-3 pr-1 py-1 hover:bg-[color:var(--brand-blue-wash)] hover:text-[color:var(--brand-blue)] transition-colors"
      data-testid={`recent-chip-${term.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
    >
      <button type="button" onClick={onSelect} className="pr-1">
        {term}
      </button>
      <button
        type="button"
        onClick={onDelete}
        aria-label={`Remove ${term} from recents`}
        data-testid={`recent-chip-delete-${term.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
        className="ml-0.5 w-4 h-4 grid place-items-center rounded-full text-slate-400 hover:text-red-500 hover:bg-white"
      >
        <X className="w-3 h-3" />
      </button>
    </span>
  );
}
